/**
 * fonts.js — Document-level Web Font Loading
 *
 * PURPOSE:
 *   Loads every web font the widget renders with, into the HOST PAGE's
 *   <head> — not the shadow root.
 *
 * WHY DOCUMENT-LEVEL (do not "tidy" this back into variables.css):
 *   @font-face rules declared inside a shadow root are NOT registered by the
 *   browser — the font silently never loads and text falls back to a generic
 *   family. Font *faces* must be defined at document level; only the
 *   font-family *usage* can live inside the shadow root. variables.css is
 *   injected into the shadow root (init.js injectSharedStyles), so the
 *   @import / @font-face rules that used to live at the top of that file were
 *   dead weight on every deployed site.
 *
 *   The same applies to the relative url()s the old @font-face rules used:
 *   the CSS is inlined into the bundle as a string (build.js ".css": "text"),
 *   so "../assets/fonts/x.ttf" resolved against the WINERY's document base URL
 *   (e.g. https://winery.com/assets/fonts/x.ttf) and 404'd everywhere.
 *   Font URLs must be absolute, off ASSETS_BASE_URL.
 *
 * SCOPE / HOST-PAGE SAFETY:
 *   This only ever appends <link>/<style> that DEFINE font families. It sets
 *   no font-family on any host-page element, so the winery's own typography is
 *   untouched. Everything is id-guarded and injected at most once.
 *
 * TENANT AWARENESS:
 *   theme.font_family picks the family (--ws-font, applyTheme in init.js).
 *   theme.font_url optionally adds ONE extra stylesheet (e.g. a Google Fonts
 *   URL) for a winery whose font isn't in SHARED_FONT_STYLESHEETS.
 *   See CONFIG_CONTRACT.md.
 *
 * USAGE:
 *   import { injectDocumentFonts } from './fonts.js';
 *   injectDocumentFonts();   // called once from initWidget()
 */

import { ASSETS_BASE_URL } from './env.js';
import { getTheme } from './configAccessors.js';
// Inlined as a data: URI by esbuild (build.js ".ttf": "dataurl"). Deliberately
// NOT fetched from ASSETS_BASE_URL: the font files aren't part of the widget
// deploy, so an external URL 404s and the winery's card titles fall back to
// the browser default serif. See EngraversRomanBTS note below.
import engraversTTF from '../assets/fonts/engravers-roman-bt.ttf';

const FACE_STYLE_ID = 'redconcierge-font-faces';
const LINK_ID_PREFIX = 'redconcierge-font-';

// Stylesheets for the families exposed as --ws-* vars in variables.css.
// "Open Sans" is the widget-wide default (--ws-font) and MUST be here: it was
// the one family every winery falls back to, yet nothing ever loaded it, so the
// widget rendered in whatever generic sans-serif the browser chose.
const SHARED_FONT_STYLESHEETS = [
  // RedConcierge wordmark matches the Red Augment website.
  'https://fonts.googleapis.com/css2?family=Manrope:wght@700&display=swap',
  // --ws-font (default)
  'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap',
  // --ws-avenir
  'https://fonts.cdnfonts.com/css/avenir',
  // --ws-lora, --ws-monst
  'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap',
  // --ws-lato
  'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap',
  // --ws-karla
  'https://fonts.googleapis.com/css2?family=Karla:ital,wght@0,200..800;1,200..800&display=swap',
];

// Self-hosted faces.
//   --ws-engraver : fogartywinery + lightningrockwinery card titles. Inlined so
//                   it renders with no CDN dependency.
//   --ws-lithos   : declared in variables.css but not referenced by any variant.
//                   Left on ASSETS_BASE_URL to keep it out of the bundle; if a
//                   winery ever uses it, publish widget/src/assets/fonts/ to
//                   <ASSETS_BASE_URL>/assets/fonts/ or inline it the same way.
function selfHostedFaceCSS() {
  const base = `${ASSETS_BASE_URL}/assets/fonts`;
  const lithos = (file, weight) => `
@font-face {
  font-family: "LithosPro";
  src: url("${base}/${file}.woff") format("woff"),
       url("${base}/${file}.otf") format("opentype");
  font-weight: ${weight};
  font-style: normal;
  font-display: swap;
}`;

  return [
    `
@font-face {
  font-family: "EngraversRomanBTS";
  src: url("${engraversTTF}") format("truetype");
  font-display: swap;
}`,
    lithos('LithosPro-ExtraLight', 200),
    lithos('LithosPro-Light', 300),
    lithos('LithosPro-Regular', 400),
    lithos('LithosPro-Bold', 700),
    lithos('LithosPro-Black', 900),
  ].join('\n');
}

function appendStylesheet(href, id) {
  if (!href || document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  // Never let a font CDN outage block the widget from rendering.
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

/**
 * Inject every font face the widget needs into document.head. Idempotent —
 * safe to call again if the widget re-initialises on the same page.
 */
export function injectDocumentFonts() {
  if (typeof document === 'undefined' || !document.head) return;

  SHARED_FONT_STYLESHEETS.forEach((href, i) => {
    appendStylesheet(href, `${LINK_ID_PREFIX}shared-${i}`);
  });

  // Optional per-winery stylesheet (tenant config theme.font_url).
  const tenantFontUrl = getTheme('font_url');
  if (tenantFontUrl) {
    appendStylesheet(String(tenantFontUrl), `${LINK_ID_PREFIX}tenant`);
  }

  if (!document.getElementById(FACE_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = FACE_STYLE_ID;
    style.textContent = selfHostedFaceCSS();
    document.head.appendChild(style);
  }
}
